import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Card, CardContent, Skeleton, Button, IconButton, Tooltip } from '@mui/material';
import { Assessment as AssessmentIcon, PictureAsPdf as PdfIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { emitOnboardingStep } from '../../onboarding/onboardingEvents';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import WarningBanner from './components/WarningBanner';
import RiskAlertBanner from './components/RiskAlertBanner';
import { includesIndustryLibrary } from './utils/libraryPrefs';
import DivergenceWarning from './components/DivergenceWarning';
import ScopeSelector from './components/ScopeSelector';
import OrgSwitcher from './components/OrgSwitcher';
import { useUserOrganizations } from './hooks/useOrganization';
import { useRiskAlerts } from './hooks/useRiskAlerts';
import QuadrantDrillDown from './components/QuadrantDrillDown';
import { CustomizableDashboard } from '../../platform/widgets/CustomizableDashboard.jsx';
import { useDashboardViewer } from '../../platform/widgets/useDashboardViewer.js';
import { DASHBOARD_KEYS } from '../../platform/widgets/widgetRegistry.js';
import { OEDashboardProvider } from './widgets/OEDashboardContext.jsx';
import { useOEDashboardData } from './widgets/useOEDashboardData.js';
import { DEFAULT_THRESHOLDS } from './utils/constants';
import { QUADRANTS } from './widgets/oeDashboardWidgets.jsx'; // registers the OE Dashboard widgets (P3-20)
import { generateOESummaryPDF } from './utils/pdfExport';
import { exportOEScoresCSV } from './utils/csvExport';

function DriftDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { queryScope } = useDemoRole();
  const viewer = useDashboardViewer();
  const drillDownRef = useRef(null);
  const [scope, setScope] = useState({ type: 'organization', id: null });
  const [expandedQuadrant, setExpandedQuadrant] = useState(null);

  // Fetch user's organization
  const { organizations, loading: orgLoading } = useUserOrganizations(user?.uid);
  const organization = organizations.length > 0 ? organizations[0] : null;
  const orgId = organization?.id || null;
  const thresholds = organization?.thresholds || DEFAULT_THRESHOLDS;

  // Site/dept/team filters belong to a specific org — reset them on org switch.
  useEffect(() => {
    setScope({ type: 'organization', id: null });
    setExpandedQuadrant(null);
  }, [orgId]);

  // Scoped members default to (and are fenced within) their own unit — the
  // ScopeSelector below also receives lockScope so they can't wander out.
  useEffect(() => {
    if (queryScope?.nwtId) setScope({ type: 'team', id: queryScope.nwtId });
    else if (queryScope?.deptId) setScope({ type: 'department', id: queryScope.deptId });
    else if (queryScope?.siteId) setScope({ type: 'site', id: queryScope.siteId });
  }, [orgId, queryScope?.nwtId, queryScope?.deptId, queryScope?.siteId]);

  // Fetch risk alerts for the organization (banner above the grid — Tim M-0818)
  const { alerts: riskAlerts, loading: alertsLoading, acknowledgeAlert: handleAcknowledgeAlert } = useRiskAlerts(orgId, user?.uid);

  // Handle quadrant click for drill-down
  const handleQuadrantClick = useCallback((quadrantKey) => {
    setExpandedQuadrant((cur) => (cur === quadrantKey ? null : quadrantKey));
  }, []);

  // One data path for every OE dashboard (B4): scores, surveys, conditions, case studies, warnings.
  const { dashboardData, scopeOptions, warnings, snapshots, latestScores, elementScores, enrichedScores, loading: dataLoading } = useOEDashboardData({
    organization,
    scope,
    queryScope,
    expandedQuadrant,
    onQuadrantClick: handleQuadrantClick,
  });
  const loading = orgLoading || dataLoading;

  // The drill-down panel renders below the grid — bring it into view when a quadrant opens.
  useEffect(() => {
    if (expandedQuadrant && drillDownRef.current) drillDownRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [expandedQuadrant]);

  // Handle PDF export
  const handleExportPDF = () => {
    generateOESummaryPDF({
      scores: latestScores,
      elementScores,
      warnings,
      organization,
      scope,
      thresholds,
    });
  };

  // Handle CSV export of OE scores
  const handleExportCSV = () => {
    try {
      exportOEScoresCSV(snapshots, organization);
    } catch (err) {
      console.error('CSV export error:', err);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          OE Dashboard
        </Typography>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  // No organization state
  if (!organization) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          OE Dashboard
        </Typography>
        <Card sx={{ mt: 3, textAlign: 'center', py: 6 }}>
          <CardContent>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Organization Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You need to create or join an organization before you can use DREW.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/settings/organization')}
            >
              Set Up Organization
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const exportTools = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={snapshots.length === 0 ? 'No snapshots to export yet' : 'Export the OE scores as CSV'}>
              <span>
                <IconButton size="small" onClick={handleExportCSV} disabled={snapshots.length === 0} aria-label="Export CSV">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Export the OE summary as PDF">
              <IconButton size="small" onClick={handleExportPDF} aria-label="Export PDF">
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );

  return (
    <Box>
      {/* Header — two rows: title, then filters (UI review 2026-09-21 §5: one non-wrapping row broke
          the title at 1440 px with the sidebar open and pushed the filters off-screen on phones). */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0, flex: '1 1 260px' }}>
            <Typography variant="h4" sx={{ mb: 0, fontSize: { xs: '1.6rem', sm: '2.125rem' }, lineHeight: 1.15 }}>
              OE Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              DREW &middot; Organizational Entropy across REPS quadrants
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
          <OrgSwitcher />
          <Box data-tour="scope-filter" sx={{ minWidth: 0 }}>
            <ScopeSelector
              value={scope}
              onChange={(next) => {
                setScope(next);
                // Onboarding: drilling into a site IS the "find the fire" move.
                if (next?.type === 'site' && next?.id) emitOnboardingStep('find-the-fire');
              }}
              organization={organization}
              lockScope={queryScope}
            />
          </Box>
        </Box>
      </Box>

      {/* Warning Alerts */}
      {warnings.length > 0 && (
        <Box sx={{ mb: 3, mt: 2 }}>
          <WarningBanner warnings={warnings} />
        </Box>
      )}

      {/* Risk Alerts from Document Analysis */}
      {!alertsLoading && riskAlerts.length > 0 && (
        <Box sx={{ mb: 2 }} data-tour="risk-alerts">
          <RiskAlertBanner
            alerts={riskAlerts}
            onAcknowledge={(alert) => handleAcknowledgeAlert(alert.id, user?.uid)}
            loading={alertsLoading}
            showIndustryRefs={includesIndustryLibrary(organization)}
          />
        </Box>
      )}

      {/* Dashboard widgets (P3-20): REPS quadrants · OE Element Breakdown · Trend · Active Surveys ·
          Recent Activity · Case Studies · Guidance — customizable per user, role default otherwise */}
      <OEDashboardProvider value={dashboardData}>
        <CustomizableDashboard dashboardKey={DASHBOARD_KEYS.drewOe} viewer={viewer} rowHeight={220} sx={{ mt: 2 }} scopeOptions={scopeOptions} toolbar={exportTools} />
      </OEDashboardProvider>

      {/* Quadrant Drill-Down Panel */}
      <Box ref={drillDownRef}>
        {expandedQuadrant && (
          <Box sx={{ mt: 3 }}>
            <QuadrantDrillDown
              quadrantKey={expandedQuadrant}
              quadrantLabel={QUADRANTS.find((q) => q.key === expandedQuadrant)?.label || ''}
              elementScores={elementScores}
              thresholds={thresholds}
              onClose={() => setExpandedQuadrant(null)}
            />
          </Box>
        )}
      </Box>

      {/* Divergence Warning — survey vs document scores */}
      {latestScores && enrichedScores && (
        <Box sx={{ mt: 3 }}>
          <DivergenceWarning surveyScores={latestScores} documentScores={enrichedScores} />
        </Box>
      )}

      {/* Empty state when no data at all */}
      {!latestScores && dashboardData.activeSurveys.length === 0 && dashboardData.surveys.length === 0 && (
        <Card sx={{ mt: 4, textAlign: 'center', py: 6 }}>
          <CardContent>
            <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Get Started with Your First Survey
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create a survey to start measuring organizational entropy across REPS quadrants.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/drew/surveys')}
            >
              Create Survey
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default DriftDashboard;
